/**
 * Maventa e-invoicing credentials (#3068, epic #3065).
 *
 * Each workshop opens its own Maventa account; OS Buddy sends invoices through
 * it. The workshop stores its Maventa company id and a user API key here and
 * tests the connection. The environment (stage or production) is a platform
 * setting and shown read-only. The API key is write-only; the API never
 * returns it.
 *
 * The test has three outcomes the workshop must tell apart: the key is wrong,
 * the key works but Maventa has not verified the company yet (Visma Sign),
 * or e-invoicing is ready.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { Alert, Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { ApiError } from '@/lib/api';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';
import {
  useDeleteMaventaCredentials,
  useMaventaIntegration,
  useSaveMaventaCredentials,
  useTestMaventaCredentials,
  type MaventaCredentialsStatus,
  type MaventaEnvironment,
} from '@/hooks/useMaventaIntegration';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ENVIRONMENT_KEYS: Record<MaventaEnvironment, string> = {
  stage: 'maventa.environmentStage',
  production: 'maventa.environmentProduction',
};

const ERROR_KEYS: Record<string, string> = {
  MAVENTA_NOT_CONFIGURED: 'maventa.errors.notConfigured',
  MAVENTA_ENCRYPTION_KEY_MISSING: 'maventa.errors.encryptionMissing',
  MAVENTA_API_KEY_REQUIRED: 'maventa.errors.apiKeyRequired',
  MAVENTA_CREDENTIALS_MISSING: 'maventa.errors.credentialsMissing',
  MAVENTA_ENVIRONMENT_MISMATCH: 'maventa.errors.environmentMismatch',
  MAVENTA_CREDENTIALS_DECRYPT_FAILED: 'maventa.errors.decryptFailed',
};

const LAST_TEST_ERROR_KEYS: Record<string, string> = {
  MAVENTA_TEST_AUTH: 'maventa.errors.testAuth',
  MAVENTA_TEST_SCOPE_MISSING: 'maventa.errors.testScope',
  MAVENTA_TEST_REJECTED: 'maventa.errors.testRejected',
  MAVENTA_TEST_HTTP: 'maventa.errors.testHttp',
  MAVENTA_TEST_TIMEOUT: 'maventa.errors.testTimeout',
  MAVENTA_TEST_NETWORK: 'maventa.errors.testNetwork',
  MAVENTA_TEST_MALFORMED: 'maventa.errors.testMalformed',
  MAVENTA_TEST_INVALID: 'maventa.errors.testInvalid',
  MAVENTA_TEST_FAILED: 'maventa.errors.testFailed',
};

function lastTestErrorKey(error: string | undefined): string {
  return (error !== undefined ? LAST_TEST_ERROR_KEYS[error] : undefined) ?? 'maventa.errors.testFailed';
}

function errorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** Tested successfully, but Maventa has not verified the company. */
function isUnverified(credentials: MaventaCredentialsStatus): boolean {
  return credentials.lastTest?.success === true && credentials.lastTest.companyState !== 'VERIFIED';
}

function statusChip(credentials: MaventaCredentialsStatus): {
  color: 'success' | 'warning' | 'error' | 'default';
  labelKey: string;
} {
  if (credentials.enabled) return { color: 'success', labelKey: 'maventa.statusReady' };
  if (credentials.lastTest?.success === false) return { color: 'error', labelKey: 'maventa.statusFailed' };
  if (isUnverified(credentials)) return { color: 'warning', labelKey: 'maventa.statusUnverified' };
  if (credentials.hasSecret) return { color: 'warning', labelKey: 'maventa.statusUntested' };
  return { color: 'default', labelKey: 'maventa.statusNotConfigured' };
}

export function MaventaSettingsTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const query = useMaventaIntegration(true);
  const saveCredentials = useSaveMaventaCredentials();
  const testCredentials = useTestMaventaCredentials();
  const deleteCredentials = useDeleteMaventaCredentials();

  const [companyId, setCompanyId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    const credentials = query.data?.credentials;
    if (!credentials) return;
    setCompanyId(credentials.companyId ?? '');
    setApiKey('');
  }, [query.data?.credentials]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={tc('loading')} />
      </Box>
    );
  }
  if (query.isError || !query.data) {
    return <Alert severity="error">{t('maventa.loadFailed')}</Alert>;
  }

  const { available, environment, credentials } = query.data;
  // A key saved for the other environment belongs to another Maventa account.
  const environmentMismatch =
    credentials.hasSecret && credentials.environment !== null && credentials.environment !== environment;
  const storedKeyUsable = credentials.hasSecret && !environmentMismatch;
  const companyIdTrimmed = companyId.trim();
  const companyIdInvalid = companyIdTrimmed !== '' && !UUID_PATTERN.test(companyIdTrimmed);

  const canSave =
    available &&
    credentials.encryptionAvailable &&
    companyIdTrimmed !== '' &&
    !companyIdInvalid &&
    (apiKey.trim() !== '' || storedKeyUsable) &&
    !saveCredentials.isPending;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      await saveCredentials.mutateAsync({
        companyId: companyIdTrimmed,
        ...(apiKey.trim() !== '' ? { apiKey: apiKey.trim() } : {}),
      });
      setApiKey('');
      notify.success(t('maventa.saved'), t('maventa.savedDesc'));
    } catch (error) {
      const known = ERROR_KEYS[errorCode(error) ?? ''];
      notify.error(tc('error'), known ? t(known) : describeApiError(error, tc, { fallback: t('maventa.errors.saveFailed') }));
    }
  };

  const canTest =
    available &&
    credentials.encryptionAvailable &&
    storedKeyUsable &&
    credentials.companyId !== null &&
    !testCredentials.isPending &&
    !saveCredentials.isPending;

  const handleTest = async () => {
    if (!canTest) return;
    try {
      const result = await testCredentials.mutateAsync();
      const tested = result.credentials;
      if (!result.success) {
        notify.error(tc('error'), t(lastTestErrorKey(tested.lastTest?.error)));
      } else if (isUnverified(tested)) {
        notify.warning(t('maventa.testUnverified'), t('maventa.testUnverifiedDesc'));
      } else {
        notify.success(t('maventa.testSuccess'), t('maventa.testSuccessDesc'));
      }
    } catch (error) {
      notify.error(tc('error'), t(ERROR_KEYS[errorCode(error) ?? ''] ?? 'maventa.errors.testFailed'));
    }
  };

  const canDelete = (credentials.hasSecret || credentials.companyId !== null) && !deleteCredentials.isPending;

  const handleDelete = async () => {
    try {
      await deleteCredentials.mutateAsync();
      setConfirmDelete(false);
      setCompanyId('');
      setApiKey('');
      notify.success(t('maventa.deleted'), t('maventa.deletedDesc'));
    } catch (error) {
      notify.error(tc('error'), t(ERROR_KEYS[errorCode(error) ?? ''] ?? 'maventa.errors.deleteFailed'));
    }
  };

  const chip = statusChip(credentials);
  const lastTest = credentials.lastTest;

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="h6">{t('maventa.title')}</Typography>
          <Chip size="small" color={chip.color} label={t(chip.labelKey)} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {t('maventa.intro')}
        </Typography>
        {environment !== null && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('maventa.environment', { environment: t(ENVIRONMENT_KEYS[environment]) })}
          </Typography>
        )}
      </Box>

      {!available && <Alert severity="info">{t('maventa.notAvailable')}</Alert>}
      {available && !credentials.encryptionAvailable && (
        <Alert severity="warning">{t('maventa.encryptionMissing')}</Alert>
      )}
      {environmentMismatch && <Alert severity="warning">{t('maventa.environmentMismatch')}</Alert>}
      {credentials.enabled && <Alert severity="success">{t('maventa.enabledHint')}</Alert>}
      {!credentials.enabled && isUnverified(credentials) && (
        <Alert severity="warning">{t('maventa.unverifiedHint')}</Alert>
      )}
      {lastTest?.success === false && (
        <Alert severity="error">
          {t(lastTestErrorKey(lastTest.error))}
          {lastTest.missingScopes !== undefined && lastTest.missingScopes.length > 0 && (
            <>
              {' '}
              {t('maventa.missingScopes', { scopes: lastTest.missingScopes.join(', ') })}
            </>
          )}
        </Alert>
      )}
      {lastTest?.companyName !== undefined && (
        <Box>
          <Typography variant="body2">
            {t('maventa.company', { name: lastTest.companyName, businessId: lastTest.companyBid ?? '—' })}
          </Typography>
          {lastTest.companyState !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {t('maventa.companyState', { state: lastTest.companyState })}
            </Typography>
          )}
        </Box>
      )}

      <Stack spacing={2}>
        <TextField
          label={t('maventa.companyId')}
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          error={companyIdInvalid}
          helperText={companyIdInvalid ? t('maventa.companyIdInvalid') : t('maventa.companyIdHint')}
          autoComplete="off"
          disabled={!available}
          required
          fullWidth
        />
        <TextField
          label={t('maventa.apiKey')}
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          helperText={storedKeyUsable ? t('maventa.apiKeySetHint') : t('maventa.apiKeyHint')}
          autoComplete="new-password"
          disabled={!available}
          fullWidth
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={saveCredentials.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSave()}
            disabled={!canSave}
          >
            {t('maventa.save')}
          </Button>
          <Button
            variant="outlined"
            startIcon={testCredentials.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => void handleTest()}
            disabled={!canTest}
          >
            {t('maventa.test')}
          </Button>
          <Button color="error" onClick={() => setConfirmDelete(true)} disabled={!canDelete}>
            {t('maventa.delete')}
          </Button>
        </Stack>
      </Stack>

      <ConfirmDialog
        open={confirmDelete}
        title={t('maventa.deleteTitle')}
        message={t('maventa.deleteMessage')}
        confirmColor="error"
        confirmText={tc('delete')}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
        loading={deleteCredentials.isPending}
      />
    </Stack>
  );
}
